import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useKeyboardLift } from '../hooks/useKeyboardLift';
import {
    IonModal,
    IonPage,
    IonContent,
    IonIcon,
    IonSearchbar,
    IonText,
    IonButton,
    IonFab,
    IonCard,
    IonCardContent,
    IonGrid,
    IonRow,
    IonCol,
    IonSkeletonText,
    useIonRouter,
} from '@ionic/react';
import { closeOutline, chevronBack, search, sparkles } from 'ionicons/icons';
import { ServiceType, ServiceTypeModalProps, ServiceInfo, serviceColorMap } from '@rsmaids/shared/types/services';
import { DataServiceFactory } from '@rsmaids/shared/services/DataServiceFactory';
import styles from './ServiceTypeModal.module.scss';
import { Keyboard } from '@capacitor/keyboard';
import { applySearchOverlayPosition } from '../utils/overlayPosition';
import { useScrollSnap } from '../hooks/useScrollSnap';
import BasePageWithHeader, { TitleCard } from './BasePageWithHeader';
import Booking from '../pages/bookingSystem/BookingForm';
import { useTranslation } from 'react-i18next';

const THUMBNAIL_FALLBACK_URL = "/assets/images/bucket.svg";
const ServiceTypeModal: React.FC<ServiceTypeModalProps> = ({
    isOpen = false,
    onClose,
    onSelectServiceType,
    selectedServiceType = '',
    services: servicesProp,
    loading: loadingProp,
    asPage = false,
}) => {
  const { t } = useTranslation();
    const router = useIonRouter();

    // In page mode the component owns its own service list + booking form.
    const [pageServices, setPageServices] = useState<ServiceInfo[]>([]);
    const [pageLoading, setPageLoading] = useState(asPage);
    const [bookingServiceType, setBookingServiceType] = useState<ServiceType | undefined>(undefined);

    useEffect(() => {
        if (!asPage) return;
        let mounted = true;
        (async () => {
            try {
                const data = await DataServiceFactory.getService().getServiceTypes();
                if (mounted) setPageServices(data);
            } catch (err) {
                console.error('[ServiceTypeModal] Failed to load services:', err);
            } finally {
                if (mounted) setPageLoading(false);
            }
        })();
        return () => { mounted = false; };
    }, [asPage]);

    const services: ServiceInfo[] = asPage ? pageServices : (servicesProp ?? []);
    const loading = asPage ? pageLoading : !!loadingProp;

    const handleClose = useCallback(() => {
        if (onClose) onClose();
        else if (router.canGoBack()) router.goBack();
        else router.push('/tabs/dashboard');
    }, [onClose, router]);
    const [searchText, setSearchText] = useState<string>('');
    const [imageErrorMap, setImageErrorMap] = useState<{ [key: number]: boolean }>({});
    const [isSearchVisible, setIsSearchVisible] = useState(false);
    const [isSearchClosing, setIsSearchClosing] = useState(false);
    const [isSearchHidden, setIsSearchHidden] = useState(false);
    const overlayRef = useRef<HTMLElement | null>(null);
    const searchRef = useRef<HTMLIonSearchbarElement | null>(null);
    const baseHeaderRef = useRef<HTMLIonHeaderElement>(null);
    const titleCardAnimationRef = useRef<HTMLIonCardElement>(null);
    const contentRef = useRef<HTMLIonContentElement>(null);
    // Inner scroll container — same pattern as BookingsListModal's
    // scrollContainerRef/bookingListContainer: a nested IonGrid with its own
    // overflow-y is the actual thing that scrolls, not IonContent directly.
    const scrollContainerRef = useRef<HTMLIonGridElement>(null);
  // Lift the floating search bar above the native keyboard (same as the other search bars)
    useKeyboardLift({ bars: () => [overlayRef.current], scroll: () => scrollContainerRef.current });

    // Header collapse animation: large title fades/shrinks away and the
    // small header title fades in as the content scrolls — same mechanism
    // as SearchModal/BookingsListModal.
    const applyProgress = useCallback((progress: number, _smooth: boolean) => {
        baseHeaderRef.current?.style.setProperty('--title-opacity', String(progress));
        if (titleCardAnimationRef.current) {
            titleCardAnimationRef.current.style.setProperty('--content-opacity', String(1 - progress));
            titleCardAnimationRef.current.style.setProperty('--content-scale', `scale(${1 - (progress * 0.2)})`);
        }
    }, []);

    const handleScroll = useScrollSnap(scrollContainerRef, 66, applyProgress);

    // Overlay positioning for morphing search (no FAB in modal, full width) —
    // same mechanism as BookingsListModal's search FAB.
    const updateOverlayPosition = useCallback(() => {
        applySearchOverlayPosition(overlayRef.current, isSearchVisible);
    }, [isSearchVisible]);

    useEffect(() => {
        updateOverlayPosition();
        window.addEventListener('resize', updateOverlayPosition);
        return () => window.removeEventListener('resize', updateOverlayPosition);
    }, [updateOverlayPosition]);

    useEffect(() => {
        const id = window.setTimeout(() => updateOverlayPosition(), 30);
        return () => window.clearTimeout(id);
    }, [isSearchVisible, updateOverlayPosition]);

    const handleToggleSearch = () => {
        if (isSearchVisible) {
            if (searchRef.current) {
                try { (searchRef.current as HTMLIonSearchbarElement).value = ''; } catch { }
            }
            setSearchText('');
            setIsSearchClosing(true);
            setIsSearchVisible(false);
            setTimeout(() => {
                setIsSearchHidden(true);
                setIsSearchClosing(false);
            }, 220);

            try {
                const s = searchRef.current as HTMLIonSearchbarElement | null;
                const getter = s ? (s as unknown as { getInputElement?: () => Promise<HTMLInputElement> }).getInputElement : undefined;
                if (getter) {
                    getter().then((inputEl: HTMLInputElement) => {
                        try { inputEl.blur(); } catch { }
                        try { Keyboard.hide(); } catch { }
                    }).catch(() => { try { Keyboard.hide(); } catch { } });
                } else {
                    try { Keyboard.hide(); } catch { }
                }
            } catch { }
        } else {
            setIsSearchHidden(false);
            setIsSearchVisible(true);
            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    try {
                        const s = searchRef.current as HTMLIonSearchbarElement | null;
                        const getter = s ? (s as unknown as { getInputElement?: () => Promise<HTMLInputElement> }).getInputElement : undefined;
                        if (getter) {
                            getter().then((inputEl: HTMLInputElement) => {
                                try { inputEl.focus(); } catch { }
                            }).catch(() => { });
                        }
                    } catch { }
                });
            });
        }
    };

    const handleImageError = (serviceIndex: number) => {
        setImageErrorMap(prev => ({ ...prev, [serviceIndex]: true }));
    };

    const handleSelectService = (type: ServiceType, imageUrl: string) => {
        if (asPage) {
            // Page mode: open our own booking form pre-set to this service.
            setBookingServiceType(type);
            return;
        }
        onSelectServiceType?.(type, imageUrl);
        onClose?.();
    };

    const filteredServices = services.filter(service =>
        typeof service.type === 'string' && service.type.toLowerCase().includes(searchText.toLowerCase())
    );

    const inner = (
        <>
                <BasePageWithHeader
                    ref={baseHeaderRef}
                    title={t('components.ServiceTypeModal.services')}
                    toolbarClassName={styles.toolbar}
                    smallTitleClassName={styles.smallTitle}
                    titleHeaderClassName={styles.titleHeader}
                    startButtons={
                        <IonButton shape="round" className={styles.closeButton} onClick={handleClose}>
                            <IonIcon icon={asPage ? chevronBack : closeOutline} />
                        </IonButton>
                    }
                    endButtons={
                        <IonButton shape="round" className={styles.searchButton} onClick={handleToggleSearch}>
                            <IonIcon icon={isSearchVisible ? closeOutline : search} />
                        </IonButton>
                    }
                >
                    <IonContent
                        ref={contentRef}
                        className={styles.content}
                        onIonScroll={(e) => handleScroll(e.detail.scrollTop)}
                    >
                        <IonGrid
                            ref={scrollContainerRef}
                            className={styles.scrollContainer}
                            onScroll={(e) => handleScroll((e.target as HTMLElement).scrollTop || 0)}
                        >
                            <div className={styles.titleCardWrapper}>
                                <TitleCard
                                    ref={titleCardAnimationRef}
                                    title={t('components.ServiceTypeModal.services')}
                                    subtitle={t('components.ServiceTypeModal.browseServiceTypesAndPricing')}
                                    subtitleClassName={styles.heroSubtitle}
                                    cardClassName={styles.hero}
                                    titleContainerClassName={styles.titleContainer}
                                    titleClassName={styles.heroTitle}
                                />
                            </div>

                        {loading ? (
                            <IonGrid className={styles.grid}>
                                <IonRow className={styles.row}>
                                    {Array.from({ length: 6 }, (_, index) => (
                                        <IonCol size="6" key={index}>
                                            <IonCard className={styles.card}>
                                                {/* Reuses .cardImage's own aspect-ratio:4/3 sizing (not a fixed
                                                    height here) so the skeleton matches the real card exactly. */}
                                                <div className={styles.cardImage}>
                                                    <IonSkeletonText animated style={{ width: '100%', height: '100%', margin: 0 }} />
                                                </div>
                                                <IonCardContent className={styles.cardBody}>
                                                    {/* Reuses the real .cardTitle <h3> (not bare IonSkeletonText with
                                                        guessed inline sizing) so font-size/line-height/min-height
                                                        all come from the same CSS the real title uses. */}
                                                    <h3 className={styles.cardTitle}>
                                                        <IonSkeletonText animated style={{ width: '90%' }} />
                                                    </h3>
                                                    {/* Reuses .selectBtn itself for the same reason — height/
                                                        border-radius/margin all come from its real CSS. */}
                                                    <IonButton className={styles.selectBtn} disabled>
                                                        <IonSkeletonText animated style={{ width: '60%' }} />
                                                    </IonButton>
                                                </IonCardContent>
                                            </IonCard>
                                        </IonCol>
                                    ))}
                                </IonRow>
                            </IonGrid>
                        ) : services.length === 0 ? (
                            <IonText className={styles.noResults}>
                                <IonIcon icon={sparkles} />
                                <p>{t('components.ServiceTypeModal.noServicesFound')}</p>
                            </IonText>
                        ) : filteredServices.length === 0 ? (
                            <IonText className={styles.noResults}>
                                <IonIcon icon={sparkles} />
                                <p>{t('components.ServiceTypeModal.noServicesMatchYourSearch')}</p>
                            </IonText>
                        ) : (
                            <IonGrid className={styles.grid}>
                                <IonRow className={styles.row}>
                                    {filteredServices.map((service, index) => {
                                        const accent = serviceColorMap[service.type] || '#5C6BC0';
                                        const imageUrl = imageErrorMap[index] ? THUMBNAIL_FALLBACK_URL : service.imageUrl;
                                        const isSelected = selectedServiceType === service.type;
                                        return (
                                            <IonCol size="6" key={service.type}>
                                                <IonCard
                                                    button
                                                    className={`${styles.card} ${isSelected ? styles.selected : ''}`}
                                                    onClick={() => handleSelectService(service.type, imageUrl)}
                                                >
                                                    <div className={styles.cardImage} style={{ borderTopColor: accent }}>
                                                        <img
                                                            src={imageUrl}
                                                            alt={service.type}
                                                            loading="lazy"
                                                            onError={() => handleImageError(index)}
                                                        />
                                                    </div>
                                                    <IonCardContent className={styles.cardBody}>
                                                        <div className={styles.cardAccent} style={{ background: accent }} />
                                                        <h3 className={styles.cardTitle}>{service.type}</h3>
                                                        <IonButton
                                                            className={styles.selectBtn}
                                                            style={{ '--accent': accent } as React.CSSProperties}
                                                            onClick={() => handleSelectService(service.type, imageUrl)}
                                                        >{t('components.ServiceTypeModal.bookNow')}
                                                        </IonButton>
                                                    </IonCardContent>
                                                </IonCard>
                                            </IonCol>
                                        );
                                    })}
                                </IonRow>
                            </IonGrid>
                        )}

                            {/* Spacer when search is visible so last items aren't hidden */}
                            {isSearchVisible && <div className={styles.searchBarSpacer} />}
                        </IonGrid>
                    </IonContent>

                    {/* Morphing search overlay (outside IonContent, inside BasePageWithHeader) */}
                    <IonFab
                        ref={el => { overlayRef.current = el; }}
                        className={`${styles.searchMorph} ${isSearchVisible ? styles.searchMorphExpanded : ''} ${isSearchClosing ? styles.searchMorphClosing : ''} ${isSearchHidden ? styles.hidden : ''}`}
                        aria-hidden={!isSearchVisible}
                    >
                        <IonSearchbar
                            ref={searchRef}
                            value={searchText}
                            debounce={50}
                            onIonInput={e => setSearchText(e.detail.value ?? '')}
                            onIonClear={() => setSearchText('')}
                            placeholder={t('components.ServiceTypeModal.searchServices', { length: services.length })}
                            animated
                            showCancelButton="never"
                            showClearButton={searchText ? 'always' : 'never'}
                            className={`${styles.searchBar} ${styles.floatingSearchBarInline} ${isSearchVisible ? styles.floatingSearchBarInlineExpanded : ''}`}
                        />
                    </IonFab>
                </BasePageWithHeader>

                {asPage && (
                    <Booking
                        isOpen={!!bookingServiceType}
                        onClose={() => setBookingServiceType(undefined)}
                        initialServiceType={bookingServiceType}
                    />
                )}
        </>
    );

    if (asPage) {
        return <IonPage className={styles.page}>{inner}</IonPage>;
    }

    return (
        <IonModal isOpen={isOpen} onDidDismiss={onClose} className={styles.serviceModal} backdropDismiss={true}>
            <div className={styles.modalWrapper}>{inner}</div>
        </IonModal>
    );
};

export default ServiceTypeModal;
